/**
 * Single source of truth for KPI definitions (ADR, occupancy, etc.).
 * Use resolveDefinition() to substitute {param} placeholders.
 */

const BASE_URL = 'https://airbtics.notion.site/Airbtics-Data-Accuracy-1f2c154c9308802fa759f22d2556068d';

export const LEARN_MORE_LINK = {
  // Main page
  dataAccuracy: BASE_URL,

  // Market KPIs
  adr: `${BASE_URL}?source=copy_link#2fec154c930880dcb6c6fc7bbe7687bf`,
  revenue: `${BASE_URL}?source=copy_link#1f2c154c930880bfab8cc2fd04f5170d`,
  occupancy: `${BASE_URL}?source=copy_link#2fec154c9308802ca157df422715f7a0`,
  calculatorMarketData: `${BASE_URL}?source=copy_link#2fec154c930880d698c5d7f12e806e90`,
  activeListings: `${BASE_URL}?source=copy_link#30bc154c93088094945ac312009eaba6`,
  seasonality: `${BASE_URL}?source=copy_link#2fec154c930880aebc9ae5f2922dad06`,
  trend: `${BASE_URL}?source=copy_link#30ac154c930880f088c8efafd53a5886`,

  // Financial Inputs - Expenses
  totalExpense: `${BASE_URL}#30bc154c93088065bfebe4bf8118f210`,
  netOperatingExpense: `${BASE_URL}#30bc154c9308801ea066efb6bff86822`,
  mortgagePayment: `${BASE_URL}#30bc154c930880d79acdebcdebd810a7`,
  taxes: `${BASE_URL}#30bc154c930880299f7adf8bfc1f6fcb`,

  // Financial Inputs - Initial Investment
  totalCashInvested: `${BASE_URL}#30bc154c9308804e8f18db0efa0e8ae6`,
  startupCost: `${BASE_URL}#30bc154c93088043b1e1d489d115d455`,

  // Performance Indicators - Cashflow
  netOperatingIncome: `${BASE_URL}#30bc154c93088052a5afc9e168a6e445`,
  netProfitBeforeTax: `${BASE_URL}#30bc154c930880e682d3e3a2cda1cb2e`,

  // Performance Indicators - Investment Metrics
  cashOnCashReturn: `${BASE_URL}#30ac154c93088092a5ccfaf169a5d494`,
  capRate: `${BASE_URL}#30ac154c930880f79e48c50f76300503`,
  grossRentalYield: `${BASE_URL}#30bc154c930880ef8c3cce8fa45570d8`,
  netRentalYield: `${BASE_URL}#30bc154c930880d68ecaee4395261750`,
  dscr: `${BASE_URL}#30bc154c930880a59776e9204aed6430`,
  roi: `${BASE_URL}#30bc154c930880ce8a22fe0156fad365`,
} as const;

export const ADR_LEARN_MORE_URL = LEARN_MORE_LINK.adr;
export const REVENUE_LEARN_MORE_URL = LEARN_MORE_LINK.revenue;
export const OCCUPANCY_LEARN_MORE_URL = LEARN_MORE_LINK.occupancy;
export const CALCULATOR_MARKET_DATA_LEARN_MORE_URL = LEARN_MORE_LINK.calculatorMarketData;
export const LEARN_MORE_TEXT = "Learn more about our data methodology and limitations here.";

export const CALCULATOR_MARKET_DATA_TOOLTIP = {
  definitionLines: ['The estimates are based on similar listings near your address.'],
  learnMoreLinkText: LEARN_MORE_TEXT,
  ctaUrl: LEARN_MORE_LINK.calculatorMarketData,
} as const;

export const KPI_DEFINITIONS = {
  ADR_market_aggregated: {
    /** Market ADR tooltip lines */
    definitionLines: [
      'How much does a host earn per typical booked night?',
      'It does not include service fees, but it does include pro-rata cleaning fees.',
    ],
    /** Full link text for Learn more */
    learnMoreLinkText: LEARN_MORE_TEXT,
    /** Legacy: use for resolveDefinition when scope param needed (e.g. MARKET_FINDER, calculator) */
    definition:
      'This shows the median booked nightly rate of Airbnb listings in {scope}. It includes total service fees (guest + host) but excludes cleaning fees and extra guest fees.',
    scopeVariants: {
      market: 'this market',
      marketLTM: 'the market over the past 12 months',
      area: 'this area',
    } as const,
  },
  ADR_listing_LTM: {
    definition:
      'This shows the average booked nightly rate of this Airbnb listing over the past 12 months. It includes total service fees (guest + host) but excludes cleaning fees and extra guest fees.',
    /** Rate trends: "The $X ADR is calculated from N observed rates..." */
    rateTrendsTemplate:
      'The {currency}{amount} ADR is calculated from {observationCount} observed rates in the past 12 months ({weekdays} weekdays + {weekends} weekends).',
    /** Filter tooltip: ADR tier filter */
    filterTierTooltip:
      'Filter by nightly price (ADR). ADR is the average booked nightly rate over the last 12 months. Tiers are based on how listings rank within this market (percentiles), not fixed price cut-offs.',
    /** ADR distribution impact */
    distributionImpact: 'See how the average daily rate of a property impacts its performance.',
  },
  Revenue_market_aggregated: {
    /** Market Revenue tooltip lines */
    definitionLines: [
      'How much does a host earn in a typical year from a short-term rental in this market?',
      'It includes cleaning and extra-guest fees, but does not include platform fees.',
    ],
    /** MARKET_FINDER: 2-bedroom variant */
    definitionLines2bd: [
      'How much does a host earn in a typical year from a typical 2-bedroom short-term rental in this market?',
      'It includes cleaning and extra-guest fees, but does not include platform fees.',
    ],
    learnMoreLinkText: LEARN_MORE_TEXT,
    ctaUrl: LEARN_MORE_LINK.revenue,
  },
  Revenue_listing_LTM: {
    /** Listing Revenue tooltip lines */
    definitionLines: [
      'How much did this listing pay out to the host over the past 12 months?',
      'It includes cleaning and extra-guest fees, but does not include platform fees.',
    ],
    learnMoreLinkText: LEARN_MORE_TEXT,
    ctaUrl: LEARN_MORE_LINK.revenue,
  },
  Revenue_potential_listing_LTM: {
    /** Revenue Potential tooltip lines */
    definitionLines: [
      'How much could this listing have paid out to the host if it were available for a full year?',
      'It includes cleaning and extra-guest fees, but does not include platform fees.',
    ],
    learnMoreLinkText: LEARN_MORE_TEXT,
    ctaUrl: LEARN_MORE_LINK.revenue,
  },
  Occupancy_market_aggregated: {
    /** Market Occupancy tooltip lines (Overview KPI section – includes range) */
    definitionLines: [
      'What percentage of nights were booked in this market?',
      'This shows the median of (booked nights ÷ available nights) for Airbnb listings in the market over the past 12 months.',
      'The range reflects uncertainty from last-minute bookings ~20%.',
    ],
    /** Without range line (calculator, market finder, etc.) */
    definitionLinesWithoutRange: [
      'What percentage of nights were booked in this market?',
      'This shows the median of (booked nights ÷ available nights) for Airbnb listings in the market over the past 12 months.',
    ],
    learnMoreLinkText: LEARN_MORE_TEXT,
    ctaUrl: LEARN_MORE_LINK.occupancy,
  },
  Occupancy_listing_LTM: {
    /** Listing Occupancy tooltip lines */
    definitionLines: ['What percentage of nights were booked for this listing?'],
    learnMoreLinkText: LEARN_MORE_TEXT,
    ctaUrl: LEARN_MORE_LINK.occupancy,
  },
} as const;

export type KpiDefinitionKey = keyof typeof KPI_DEFINITIONS;

/** Resolve {param} placeholders. Use _template to pick a variant (e.g. 'rateTrendsTemplate'). */
export function resolveDefinition(
  key: KpiDefinitionKey,
  params?: Record<string, string | number> & { _template?: string }
): string {
  const def = KPI_DEFINITIONS[key] as Record<string, unknown>;
  const { _template, ...rest } = params ?? {};
  const template = _template && typeof def[_template] === 'string' ? (def[_template] as string) : def.definition as string;
  let text = template;
  Object.entries(rest).forEach(([k, v]) => {
    text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
  });
  return text;
}
